import React, { useMemo, useRef, Suspense } from 'react'
import { useTexture } from '@react-three/drei'
import { useDesignStore } from '../../store/designStore'
import * as THREE from 'three'

interface TerrainFromImageProps {
  heightScale?: number
  segments?: number
  buildingAreaSize?: number
}

const TerrainFromImageInner: React.FC<TerrainFromImageProps> = ({ 
  heightScale = 3, 
  segments = 64,
  buildingAreaSize = 0.25
}) => {
  const { canvasSettings } = useDesignStore()
  const meshRef = useRef<THREE.Mesh>(null)
  
  if (!canvasSettings.backgroundImage) {
    return null
  }

  // Load the texture for both height mapping and surface texture
  const texture = useTexture(canvasSettings.backgroundImage, (texture) => {
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.flipY = false
  })

  // Create terrain geometry from image data
  const terrainGeometry = useMemo(() => {
    if (!texture || !texture.image) return null

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    // Set canvas size to match segments for height map sampling
    canvas.width = segments + 1
    canvas.height = segments + 1
    
    // Draw image to canvas for pixel data extraction
    ctx.drawImage(texture.image, 0, 0, canvas.width, canvas.height)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    
    // Create plane geometry
    const geometry = new THREE.PlaneGeometry(
      canvasSettings.width, 
      canvasSettings.height, 
      segments, 
      segments
    )
    
    // Get position attribute
    const positions = geometry.attributes.position.array as Float32Array
    
    // First pass: Calculate raw height data with center flattening
    const heightMap: number[] = []
    const centerX = (segments + 1) / 2
    const centerY = (segments + 1) / 2
    const buildingRadius = Math.min(segments, segments) * buildingAreaSize // Configurable building area size
    
    for (let i = 0; i < positions.length; i += 3) {
      const x = i / 3
      const col = x % (segments + 1)
      const row = Math.floor(x / (segments + 1))
      
      // Calculate distance from center for building area
      const distanceFromCenter = Math.sqrt(
        Math.pow(col - centerX, 2) + Math.pow(row - centerY, 2)
      )
      
      // Get pixel data (RGBA)
      const pixelIndex = (row * canvas.width + col) * 4
      const r = imageData.data[pixelIndex]
      const g = imageData.data[pixelIndex + 1]
      const b = imageData.data[pixelIndex + 2]
      
      const brightness = (r + g + b) / 3 / 255
      
      // Create a flat building area in the center, with 3D terrain around the edges
      let height = brightness
      
      if (distanceFromCenter < buildingRadius) {
        // Center area: flat building zone
        height = 0.1 // Very flat for construction
      } else {
        // Outer areas: create 3D environment based on image
        const edgeDistance = distanceFromCenter - buildingRadius
        const maxEdgeDistance = Math.sqrt(2) * (segments + 1) / 2 - buildingRadius
        const edgeFactor = Math.min(edgeDistance / maxEdgeDistance, 1)
        
        // Use image data to create surrounding 3D environment
        // Darker areas become valleys, lighter areas become hills
        if (brightness < 0.3) {
          // Dark areas: create valleys or water features
          height = -0.2 + brightness * 0.4
        } else if (brightness > 0.7) {
          // Light areas: create hills or elevated features
          height = 0.3 + (brightness - 0.7) * 2
        } else {
          // Mid-tones: gentle slopes
          height = brightness * 0.6
        }
        
        // Apply edge factor to make terrain more dramatic at edges
        height *= edgeFactor * 2
        
        // Add some randomness for natural look
        const noise = (Math.random() - 0.5) * 0.1
        height += noise
      }
      
      heightMap.push(height)
    }
    
    // Second pass: Apply smoothing to create realistic terrain (preserve flat center)
    const smoothedHeights: number[] = []
    for (let i = 0; i < heightMap.length; i++) {
      const row = Math.floor(i / (segments + 1))
      const col = i % (segments + 1)
      
      // Calculate distance from center
      const distanceFromCenter = Math.sqrt(
        Math.pow(col - centerX, 2) + Math.pow(row - centerY, 2)
      )
      
      if (distanceFromCenter < buildingRadius) {
        // Keep building area completely flat
        smoothedHeights.push(0.1)
      } else {
        // Apply smoothing only to surrounding terrain
        let smoothedHeight = 0
        let weightSum = 0
        
        for (let dy = -2; dy <= 2; dy++) {
          for (let dx = -2; dx <= 2; dx++) {
            const nRow = row + dy
            const nCol = col + dx
            
            if (nRow >= 0 && nRow < segments + 1 && nCol >= 0 && nCol < segments + 1) {
              const nIndex = nRow * (segments + 1) + nCol
              const distance = Math.sqrt(dx * dx + dy * dy)
              const weight = Math.exp(-distance * distance / 2) // Gaussian weight
              
              smoothedHeight += heightMap[nIndex] * weight
              weightSum += weight
            }
          }
        }
        
        smoothedHeights.push(smoothedHeight / weightSum)
      }
    }
    
    // Apply the smoothed heights - flat center, dramatic edges
    for (let i = 0; i < positions.length; i += 3) {
      const heightIndex = i / 3
      const row = Math.floor(heightIndex / (segments + 1))
      const col = heightIndex % (segments + 1)
      
      // Calculate distance from center
      const distanceFromCenter = Math.sqrt(
        Math.pow(col - centerX, 2) + Math.pow(row - centerY, 2)
      )
      
      if (distanceFromCenter < buildingRadius) {
        // Keep building area completely flat
        positions[i + 2] = 0
      } else {
        // Apply full height scale to surrounding terrain for dramatic 3D environment
        positions[i + 2] = smoothedHeights[heightIndex] * heightScale
      }
    }
    
    // Recalculate normals for proper lighting
    geometry.computeVertexNormals()
    
    return geometry
  }, [texture, segments, heightScale, canvasSettings.width, canvasSettings.height])

  // Create a normal map from the height data for enhanced detail
  const normalMap = useMemo(() => {
    if (!texture || !texture.image) return null

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    const size = 256
    canvas.width = size
    canvas.height = size
    
    // Draw original image
    ctx.drawImage(texture.image, 0, 0, size, size)
    const imageData = ctx.getImageData(0, 0, size, size)
    
    // Create normal map data
    const normalData = new Uint8Array(size * size * 4)
    
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const i = (y * size + x) * 4
        
        // Sample neighboring pixels for gradient calculation
        const left = x > 0 ? imageData.data[((y * size + (x - 1)) * 4)] : imageData.data[i]
        const right = x < size - 1 ? imageData.data[((y * size + (x + 1)) * 4)] : imageData.data[i]
        const up = y > 0 ? imageData.data[(((y - 1) * size + x) * 4)] : imageData.data[i]
        const down = y < size - 1 ? imageData.data[(((y + 1) * size + x) * 4)] : imageData.data[i]
        
        // Calculate gradients
        const dx = (right - left) / 255
        const dy = (down - up) / 255
        
        // Calculate normal vector
        const normal = new THREE.Vector3(-dx, -dy, 1).normalize()
        
        // Convert to 0-255 range
        normalData[i] = Math.floor((normal.x + 1) * 127.5)     // R
        normalData[i + 1] = Math.floor((normal.y + 1) * 127.5) // G
        normalData[i + 2] = Math.floor((normal.z + 1) * 127.5) // B
        normalData[i + 3] = 255 // A
      }
    }
    
    // Create texture from normal data
    const normalTexture = new THREE.DataTexture(normalData, size, size, THREE.RGBAFormat)
    normalTexture.needsUpdate = true
    normalTexture.wrapS = THREE.ClampToEdgeWrapping
    normalTexture.wrapT = THREE.ClampToEdgeWrapping
    
    return normalTexture
  }, [texture])

  if (!terrainGeometry) return null

  const terrainPosition = useMemo(() => {
    return [
      canvasSettings.backgroundImageOffsetX || 0,
      canvasSettings.backgroundImageOffsetY || 0,
      canvasSettings.backgroundImageOffsetZ || 0
    ] as [number, number, number]
  }, [canvasSettings.backgroundImageOffsetX, canvasSettings.backgroundImageOffsetY, canvasSettings.backgroundImageOffsetZ])

  const terrainRotation = useMemo(() => {
    return [
      -Math.PI / 2 + (canvasSettings.backgroundImageRotationX || 0),
      canvasSettings.backgroundImageRotationY || 0,
      canvasSettings.backgroundImageRotationZ || 0
    ] as [number, number, number]
  }, [canvasSettings.backgroundImageRotationX, canvasSettings.backgroundImageRotationY, canvasSettings.backgroundImageRotationZ])

  const terrainScale = useMemo(() => {
    const scale = canvasSettings.backgroundImageScale || 1.0
    return [scale, 1, scale] as [number, number, number] // Scale X and Z, but not Y (height)
  }, [canvasSettings.backgroundImageScale])

  return (
    <mesh
      ref={meshRef}
      geometry={terrainGeometry}
      rotation={terrainRotation} // Use dynamic rotation to match image
      position={terrainPosition} // Use dynamic position to match image
      scale={terrainScale} // Use dynamic scale to match image
      receiveShadow
      castShadow
    >
      <meshStandardMaterial
        map={texture}
        normalMap={normalMap}
        roughness={0.9} // More matte for natural ground
        metalness={0.0} // No metallic properties for terrain
        transparent={false}
        opacity={1}
        side={THREE.DoubleSide}
        // Enhanced terrain material properties for buildable ground
        normalScale={new THREE.Vector2(0.5, 0.5)} // Subtle normal mapping
        displacementScale={0}
        // Add slight color tinting for more natural ground appearance
        color="#f5f5dc" // Beige tint for natural ground
      />
    </mesh>
  )
}

const TerrainFromImage: React.FC<TerrainFromImageProps> = (props) => {
  const { canvasSettings } = useDesignStore()
  
  if (!canvasSettings.backgroundImage) {
    return null
  }

  return (
    <Suspense fallback={null}>
      <TerrainFromImageInner {...props} />
    </Suspense>
  )
}

export default TerrainFromImage
